import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AccountBalanceWallet,
  CheckCircle,
  Cancel,
  Refresh,
  Visibility,
  VerifiedUser,
} from "@mui/icons-material";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { brandPalette as primaryBlue } from "../config/brandTheme";
import {
  ULTRA_PLUS_FIELDS,
  ULTRA_PLUS_TS_FIELDS,
  ULTRA_PLUS_VBOOST_FIELDS,
  ULTRA_PLUS_TS_WALLET_AMOUNT,
  ULTRA_PLUS_VBOOST_WALLET_AMOUNT,
} from "../config/ultraPlusFields";

const renderFieldReview = ({
  key,
  label,
  request,
  review,
  readOnly,
  setFieldReviews,
}) => {
  const borderColor =
    review === "approved"
      ? "success.main"
      : review === "rejected"
        ? "error.main"
        : "divider";

  return (
    <Box
      key={key}
      sx={{
        mb: 2,
        p: 2,
        border: "2px solid",
        borderColor,
        borderRadius: 1,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        {readOnly && review && (
          <Chip
            size="small"
            color={review === "approved" ? "success" : "error"}
            label={review === "approved" ? "Approved" : "Rejected"}
          />
        )}
      </Box>
      <Typography
        variant="body2"
        sx={{
          mb: 1,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          bgcolor: "grey.50",
          p: 1.5,
          borderRadius: 1,
        }}
      >
        {request[key] || "—"}
      </Typography>
      {!readOnly && (
        <ButtonGroup size="small" variant="outlined">
          <Button
            color="success"
            variant={review === "approved" ? "contained" : "outlined"}
            startIcon={<CheckCircle />}
            onClick={() => setFieldReviews((prev) => ({ ...prev, [key]: "approved" }))}
          >
            Approve
          </Button>
          <Button
            color="error"
            variant={review === "rejected" ? "contained" : "outlined"}
            startIcon={<Cancel />}
            onClick={() => setFieldReviews((prev) => ({ ...prev, [key]: "rejected" }))}
          >
            Reject
          </Button>
        </ButtonGroup>
      )}
    </Box>
  );
};

const UltraPlusApprovalManagement = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [processingId, setProcessingId] = useState(null);
  const [walletProcessing, setWalletProcessing] = useState(null);
  const [reviewDialog, setReviewDialog] = useState({ open: false, request: null, readOnly: false });
  const [rejectionReason, setRejectionReason] = useState("");
  const [fieldReviews, setFieldReviews] = useState({});


  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError("");
      const snapshot = await getDocs(
        query(
          collection(db, "ultraPlusApprovalRequests"),
          orderBy("createdAt", "desc")
        )
      );
      setRequests(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    } catch (err) {
      console.error("Error fetching Ultra Plus requests:", err);
      setError("Failed to fetch Ultra Plus approval requests: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const stats = useMemo(() => {
    return {
      pending: requests.filter((r) => r.status === "pending").length,
      approved: requests.filter((r) => r.status === "approved").length,
      rejected: requests.filter(
        (r) => r.status === "rejected" || r.status === "partially_rejected"
      ).length,
    };
  }, [requests]);

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    try {
      const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
      return date.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch (e) {
      return "Invalid Date";
    }
  };

  const getStatusChip = (status) => {
    const config = {
      pending: { color: "warning", label: "Pending" },
      approved: { color: "success", label: "Approved" },
      partially_rejected: { color: "error", label: "Partial Rejection" },
      rejected: { color: "error", label: "Rejected" },
    };
    const current = config[status] || { color: "default", label: status || "Unknown" };
    return <Chip size="small" color={current.color} label={current.label} />;
  };

  const openReviewDialog = (request, readOnly = false) => {
    const initialReviews = ULTRA_PLUS_FIELDS.reduce((acc, { key }) => {
      if (readOnly && request.fieldReviews?.[key]) {
        acc[key] = request.fieldReviews[key];
      } else {
        acc[key] = null;
      }
      return acc;
    }, {});
    setFieldReviews(initialReviews);
    setRejectionReason(readOnly ? request.rejectionReason || "" : "");
    setReviewDialog({ open: true, request, readOnly });
  };

  const allFieldsReviewed = ULTRA_PLUS_FIELDS.every(
    ({ key }) => fieldReviews[key] === "approved" || fieldReviews[key] === "rejected"
  );

  const rejectedFieldKeys = ULTRA_PLUS_FIELDS.filter(
    ({ key }) => fieldReviews[key] === "rejected"
  ).map(({ key }) => key);

  const approvedFieldKeys = ULTRA_PLUS_FIELDS.filter(
    ({ key }) => fieldReviews[key] === "approved"
  ).map(({ key }) => key);

  const handleAddWalletCredit = async (type) => {
    const request = reviewDialog.request;
    if (!request) return;

    const isTs = type === "ts";
    const amount = isTs ? ULTRA_PLUS_TS_WALLET_AMOUNT : ULTRA_PLUS_VBOOST_WALLET_AMOUNT;
    const flagField = isTs ? "tsWalletCreditAdded" : "vBoostWalletCreditAdded";
    const label = isTs ? "T&S" : "V-Boost";

    if (request[flagField]) {
      setError(`₹${amount} ${label} wallet credit was already added for this request.`);
      return;
    }

    try {
      setWalletProcessing(type);
      setError("");
      setSuccess("");

      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", request.userId)
      );
      const userSnapshot = await getDocs(userQuery);

      if (userSnapshot.empty) {
        setError("User not found");
        return;
      }

      const userDoc = userSnapshot.docs[0];
      const userData = userDoc.data();
      const now = new Date().toISOString();
      const currentBalance = userData.cashbackBalance || 0;
      const currentEarned = userData.cashbackEarned || 0;

      await updateDoc(doc(db, "users", userDoc.id), {
        cashbackBalance: currentBalance + amount,
        cashbackEarned: currentEarned + amount,
        updatedAt: now,
      });

      await updateDoc(doc(db, "ultraPlusApprovalRequests", request.id), {
        [flagField]: true,
        [`${type}WalletCreditAmount`]: amount,
        [`${type}WalletCreditAddedAt`]: now,
        updatedAt: now,
      });

      const updatedRequest = {
        ...request,
        [flagField]: true,
        [`${type}WalletCreditAmount`]: amount,
        [`${type}WalletCreditAddedAt`]: now,
      };

      setReviewDialog((prev) => ({ ...prev, request: updatedRequest }));
      setRequests((prev) =>
        prev.map((item) => (item.id === request.id ? updatedRequest : item))
      );
      setSuccess(
        `Added ₹${amount} to ${request.userName}'s Cashback Balance (${label} reward).`
      );
    } catch (err) {
      console.error("Error adding wallet credit:", err);
      setError("Failed to add wallet credit: " + err.message);
    } finally {
      setWalletProcessing(null);
    }
  };

  const renderWalletCreditBox = (type) => {
    const request = reviewDialog.request;
    if (!request) return null;

    const isTs = type === "ts";
    const amount = isTs ? ULTRA_PLUS_TS_WALLET_AMOUNT : ULTRA_PLUS_VBOOST_WALLET_AMOUNT;
    const flagField = isTs ? "tsWalletCreditAdded" : "vBoostWalletCreditAdded";
    const label = isTs ? "T&S" : "V-Boost";
    const alreadyAdded = request[flagField];

    return (
      <Paper
        variant="outlined"
        sx={{
          mb: 3,
          p: 2,
          bgcolor: alreadyAdded ? "success.50" : "primary.50",
          borderColor: alreadyAdded ? "success.main" : "primary.main",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {label} Cashback Reward
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Adds ₹{amount} to the user&apos;s Cashback Balance for purchases.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color={alreadyAdded ? "success" : "primary"}
            startIcon={
              walletProcessing === type ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <AccountBalanceWallet />
              )
            }
            onClick={() => handleAddWalletCredit(type)}
            disabled={alreadyAdded || walletProcessing !== null}
          >
            {alreadyAdded ? `Added ₹${amount} to wallet` : `Add ${amount} to wallet`}
          </Button>
        </Box>
      </Paper>
    );
  };

  const handleSubmitReview = async () => {
    if (!reviewDialog.request || !allFieldsReviewed) return;

    try {
      setProcessingId(reviewDialog.request.id);
      setError("");
      setSuccess("");

      const request = reviewDialog.request;
      const approvedFields = approvedFieldKeys;
      const rejectedFields = rejectedFieldKeys;

      let status = "approved";
      if (rejectedFields.length === ULTRA_PLUS_FIELDS.length) {
        status = "rejected";
      } else if (rejectedFields.length > 0) {
        status = "partially_rejected";
      }

      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", request.userId)
      );
      const userSnapshot = await getDocs(userQuery);

      if (userSnapshot.empty) {
        setError("User not found");
        return;
      }

      const userDocRef = doc(db, "users", userSnapshot.docs[0].id);
      const userData = userSnapshot.docs[0].data();
      const now = new Date().toISOString();

      const approvedValues = approvedFields.reduce((acc, key) => {
        acc[key] = (request[key] || userData[key] || "").trim();
        return acc;
      }, {});

      const userUpdate = {
        ...approvedValues,
        ultraPlusApprovedFields: approvedFields,
        ultraPlusRejectedFields: rejectedFields,
        ultraPlusApproved: status === "approved",
        ultraPlusStatus: status,
        updatedAt: now,
      };

      if (status === "approved") {
        userUpdate.ultraPlusApprovedAt = now;
      } else {
        userUpdate.ultraPlusRejectedAt = now;
      }

      if (status === "approved") {
        const vBoostOk = ULTRA_PLUS_VBOOST_FIELDS.every(({ key }) =>
          approvedFields.includes(key)
        );
        if (vBoostOk) {
          userUpdate.autoFillVBoostVerified = true;
          userUpdate.autoFillVBoostVerifiedAt = now;
        }
        const tsOk = ULTRA_PLUS_TS_FIELDS.every(({ key }) =>
          approvedFields.includes(key)
        );
        if (tsOk) {
          userUpdate.autoFillProgramWalletVerified = true;
          userUpdate.autoFillProgramWalletVerifiedAt = now;
        }
      }

      await updateDoc(userDocRef, userUpdate);

      await updateDoc(doc(db, "ultraPlusApprovalRequests", request.id), {
        status,
        approvedAt: status === "approved" ? now : null,
        rejectedAt: status !== "approved" ? now : null,
        updatedAt: now,
        fieldReviews: { ...fieldReviews },
        approvedFields,
        rejectedFields,
        rejectionReason: rejectedFields.length
          ? rejectionReason.trim() || "Please update the rejected fields."
          : null,
        reviewedBy: "admin",
      });

      const statusMessages = {
        approved: `All Ultra Plus details approved for ${request.userName}.`,
        partially_rejected: `Partial review completed for ${request.userName}. Approved fields saved; user notified to update rejected fields.`,
        rejected: `Ultra Plus request rejected for ${request.userName}. User notified by email.`,
      };

      setSuccess(statusMessages[status]);
      setReviewDialog({ open: false, request: null, readOnly: false });
      setRejectionReason("");
      fetchRequests();
    } catch (err) {
      console.error("Error submitting Ultra Plus review:", err);
      setError("Failed to submit review: " + err.message);
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <CircularProgress size={48} />
        <Typography sx={{ mt: 2 }}>Loading Ultra Plus approval requests...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ color: primaryBlue[800], fontWeight: 700 }}>
            Ultra Plus Approval
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review each field individually. Approve valid fields and reject fields that need updates.
          </Typography>
        </Box>
        <Button startIcon={<Refresh />} onClick={fetchRequests} variant="outlined">
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Card sx={{ minWidth: 160 }}>
          <CardContent>
            <Typography variant="h4" color="warning.main">{stats.pending}</Typography>
            <Typography variant="body2">Pending</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 160 }}>
          <CardContent>
            <Typography variant="h4" color="success.main">{stats.approved}</Typography>
            <Typography variant="body2">Approved</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 160 }}>
          <CardContent>
            <Typography variant="h4" color="error.main">{stats.rejected}</Typography>
            <Typography variant="body2">Rejected / Partial</Typography>
          </CardContent>
        </Card>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  No Ultra Plus approval requests yet.
                </TableCell>
              </TableRow>
            ) : (
              requests.map((request) => (
                <TableRow key={request.id} hover>
                  <TableCell>{request.userName || "N/A"}</TableCell>
                  <TableCell>{request.userEmail || "N/A"}</TableCell>
                  <TableCell>{getStatusChip(request.status)}</TableCell>
                  <TableCell>{formatDate(request.createdAt)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={request.status === "pending" ? "Review details" : "View review"}>
                      <IconButton
                        color="primary"
                        onClick={() => openReviewDialog(request, request.status !== "pending")}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={reviewDialog.open}
        onClose={() => setReviewDialog({ open: false, request: null, readOnly: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <VerifiedUser color="primary" />
          {reviewDialog.readOnly ? "Ultra Plus Review Details" : "Review Ultra Plus Details"}
        </DialogTitle>
        <DialogContent dividers>
          {reviewDialog.request && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {reviewDialog.request.userName} ({reviewDialog.request.userEmail})
              </Typography>

              <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                T&amp;S Details
              </Typography>
              {ULTRA_PLUS_TS_FIELDS.map((field) =>
                renderFieldReview({
                  ...field,
                  request: reviewDialog.request,
                  review: fieldReviews[field.key],
                  readOnly: reviewDialog.readOnly,
                  setFieldReviews,
                })
              )}

              {renderWalletCreditBox("ts")}

              <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1, mt: 1 }}>
                V-Boost Details
              </Typography>
              {ULTRA_PLUS_VBOOST_FIELDS.map((field) =>
                renderFieldReview({
                  ...field,
                  request: reviewDialog.request,
                  review: fieldReviews[field.key],
                  readOnly: reviewDialog.readOnly,
                  setFieldReviews,
                })
              )}

              {renderWalletCreditBox("vboost")}

              {!reviewDialog.readOnly && !allFieldsReviewed && (
                <Alert severity="info">
                  Mark each field as Approve or Reject before submitting your review.
                </Alert>
              )}

              {!reviewDialog.readOnly && rejectedFieldKeys.length > 0 && (
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Note for user (optional)"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  helperText="This note will be included in the email for rejected fields."
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialog({ open: false, request: null, readOnly: false })}>
            Close
          </Button>
          {!reviewDialog.readOnly && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckCircle />}
              onClick={handleSubmitReview}
              disabled={!allFieldsReviewed || processingId === reviewDialog.request?.id}
            >
              Submit Review
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UltraPlusApprovalManagement;
